import { useState, useEffect } from 'react';

type UsePersistedStateOptions = {
	key: string;
	storage?: Storage;
};

export function usePersistedState<T>(
	initialValue: T,
	options: UsePersistedStateOptions
): [T, React.Dispatch<React.SetStateAction<T>>] {
	const { key, storage = localStorage } = options;

	const [state, setState] = useState<T>(() => {
		try {
			const saved = storage.getItem(key);
			return saved ? JSON.parse(saved) : initialValue;
		} catch (error) {
			console.warn(
				`Failed to parse persisted state from ${
					storage === localStorage ? 'localStorage' : 'sessionStorage'
				} for key "${key}":`,
				error
			);
			return initialValue;
		}
	});

	useEffect(() => {
		try {
			storage.setItem(key, JSON.stringify(state));
		} catch (error) {
			console.warn(
				`Failed to save persisted state to ${
					storage === localStorage ? 'localStorage' : 'sessionStorage'
				} for key "${key}":`,
				error
			);
		}
	}, [key, state, storage]);

	return [state, setState];
}
